import HighchartsReact from 'highcharts-react-official';
import { MetricDefinitionPopOver } from 'modules/productAnalytics';
import {
  formatNumberGivenValueType,
  getChartActions,
  getChartExportingOptions,
} from 'modules/productAnalytics/utils';
import { useRef } from 'react';
import { useLocation } from 'react-router';
import { NUMBER } from 'ui/card/constants';
import { FlexSpacer } from 'ui/flexSpacer';
import { NoResult } from 'ui/illustrations';
import { Skeleton } from 'ui/skeleton';
import { WebLink2 } from 'ui/theme';
import { graphColorPalette } from '../constants';
import { useReplyOverviewStats } from '../hooks';
import { ViewState } from '../types';
import { CampaignAnalyticsPieChart } from './CampaignAnalyticsPieChart';
import { MetricBreakdownCard } from './MetricBreakdownCard';
import { NoDataCard } from './NoDataCard';
import {
  GraphContainer,
  MetricBreakdownHeaderTitle,
  MetricBreakdownHeaderValue,
  MetricBreakdownMainMetric,
  MetricList,
  MetricRow,
  OverviewCardContainer,
  OverviewRow,
  SkeletonWrapper,
  StyledDivider,
  StyledListContainer,
} from './Styled';

type RepliesOverviewContentProps = {
  viewState: ViewState;
  totalReplies?: number;
  totalQuickReplies?: number;
  totalCustomReplies?: number;
  chartRef: React.RefObject<HighchartsReact>;
};

const tooltip = 'Total number of WhatsApp messages received in response to a campaign.';

const ColorIndicator = ({ color }: { color: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
    <circle cx="4" cy="4" r="4" fill={color} />
  </svg>
);

const RepliesOverviewContent = ({
  viewState,
  totalReplies,
  totalQuickReplies,
  totalCustomReplies,
  chartRef,
}: RepliesOverviewContentProps) => {
  switch (viewState) {
    case ViewState.Loading:
      return (
        <SkeletonWrapper height={642} padding={24}>
          <Skeleton />
        </SkeletonWrapper>
      );
    case ViewState.NoData:
      return (
        <FlexSpacer alignItems="center" justifyContent="center" height={540}>
          <NoDataCard
            Icon={NoResult}
            title="Looks like you don’t have any replies yet for this campaign"
            subtitle={
              <>
                <WebLink2 target="_blank" rel="noopener noreferrer" href="#">
                  Click here
                </WebLink2>{' '}
                for tips on how to drive conversations with customers.
              </>
            }
          />
        </FlexSpacer>
      );
    case ViewState.ContentReady:
      return (
        <OverviewCardContainer>
          <OverviewRow>
            <MetricBreakdownMainMetric>
              <MetricBreakdownHeaderTitle data-test="metric-title-replies-overview">
                <MetricDefinitionPopOver definition={tooltip}>
                  <span>Total replies</span>
                </MetricDefinitionPopOver>
              </MetricBreakdownHeaderTitle>
              <MetricBreakdownHeaderValue data-test="metric-value-replies-overview">
                {formatNumberGivenValueType(NUMBER, totalReplies)}
              </MetricBreakdownHeaderValue>
            </MetricBreakdownMainMetric>
          </OverviewRow>
          <StyledDivider />
          <GraphContainer>
            <CampaignAnalyticsPieChart
              data={[
                {
                  y: (totalQuickReplies ?? 0) / (totalReplies ?? 1),
                  name: 'Quick reply',
                },
                {
                  y: (totalCustomReplies ?? 0) / (totalReplies ?? 1),
                  name: 'Custom reply',
                },
              ]}
              ref={chartRef}
            />
          </GraphContainer>
          <StyledDivider />
          <StyledListContainer>
            <MetricList>
              <MetricRow>
                <FlexSpacer gap="half">
                  <ColorIndicator color={graphColorPalette[0]} />
                  <span data-test={`metric-title-quick-replies`}>Quick reply</span>
                </FlexSpacer>
                <span data-test={`metric-value-quick-replies`}>
                  {formatNumberGivenValueType(NUMBER, totalQuickReplies)}
                </span>
              </MetricRow>
            </MetricList>
            <MetricList>
              <MetricRow>
                <FlexSpacer gap="half">
                  <ColorIndicator color={graphColorPalette[1]} />
                  <span data-test={`metric-title-custom-replies`}>Custom reply</span>
                </FlexSpacer>
                <span data-test={`metric-value-custom-replies`}>
                  {formatNumberGivenValueType(NUMBER, totalCustomReplies)}
                </span>
              </MetricRow>
            </MetricList>
          </StyledListContainer>
        </OverviewCardContainer>
      );
    default:
      return null;
  }
};

export const RepliesOverview = () => {
  let viewState: ViewState;
  const { search } = useLocation();
  const chartRef = useRef<HighchartsReact | null>(null);
  const chartId = 'repliesOverviewChart';
  const chartActions = getChartActions(chartId, chartRef);
  const {
    data: { campaignReplyStatsOverview } = {},
    isLoading,
  } = useReplyOverviewStats();
  const { totalReplies, totalCustomReplies, totalQuickReplies } = campaignReplyStatsOverview || {};

  if (isLoading) {
    viewState = ViewState.Loading;
  } else if (totalReplies === 0 || !campaignReplyStatsOverview) {
    viewState = ViewState.NoData;
  } else {
    viewState = ViewState.ContentReady;
  }

  const showMenu = viewState === ViewState.ContentReady;

  return (
    <MetricBreakdownCard
      title={'Replies'}
      linkout={{
        href: '/analytics/campaignPerformance/replies',
        search,
      }}
      menuOptions={showMenu ? getChartExportingOptions(chartId) : undefined}
      handleSelectionChange={(key) => chartActions[key]()}
      data-test="replies-overview"
    >
      <RepliesOverviewContent
        viewState={viewState}
        totalReplies={totalReplies}
        totalQuickReplies={totalQuickReplies}
        totalCustomReplies={totalCustomReplies}
        chartRef={chartRef}
      />
    </MetricBreakdownCard>
  );
};
